import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Link, useParams } from 'react-router-dom';
import { Icon } from 'js/components';
import { scaleColorTraffic } from 'js/utils/color';
import { getQuestionSetCategory } from 'js/utils/question-set';
import { titleCase } from 'js/utils/string';
import { CategoryPills } from 'js/components/Charts/CategoryLayout/CategoryPills';
import categoryLayoutStyles from 'js/components/Charts/CategoryLayout/CategoryLayout.module.scss';
import NullMarker from 'js/engines/SurveyAssessment/components/Review/NullMarker';
import { faCalendarAlt } from '@fortawesome/pro-regular-svg-icons';

import styles from './AssessmentItem.module.scss';

const UNKNOWN_ASSESSMENT_KEY = '__unknown_assessment_key__';
const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

function formatAssessmentTimestamp(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return dateFormatter.format(date);
}

function rubricQuestionTripleKey(assessmentKey, source) {
  if (!assessmentKey || !source) return null;
  const questionKey = source.key || source.question_key;
  if (!source.question_set_key || !questionKey) return null;
  return `${assessmentKey}|${source.question_set_key}|${questionKey}`;
}

function buildRubricCategoryLookups(questionSets) {
  const keys = [
    ...new Set(questionSets.map((qs) => getQuestionSetCategory(qs))),
  ].sort();
  const map = new Map();
  let ci = 0;
  keys.forEach((k) => {
    map.set(k, {
      display_name: titleCase(String(k).replace(/-/g, ' ')),
      palette: `palette-cat-${++ci}`,
    });
  });
  return map;
}

function questionSetContributesToAssessment(questionSet, assessmentKey) {
  return (questionSet.questions || []).some((question) =>
    (question.sources || []).some(
      (source) => source.assessment_key === assessmentKey
    )
  );
}

function includedCategoryKeysInOrder(questionSets, assessmentKey) {
  const out = [];
  const seen = new Set();
  for (const qs of questionSets) {
    if (!questionSetContributesToAssessment(qs, assessmentKey)) continue;
    const cat = getQuestionSetCategory(qs);
    if (seen.has(cat)) continue;
    seen.add(cat);
    out.push(cat);
  }
  return out;
}

export default function AssessmentItem({
  assessment,
  questionSets = [],
  assessmentScoreMaps = {},
  className = '',
}) {
  const { workspaceId } = useParams();
  const assessmentId = String(assessment.assessment_id ?? assessment.id ?? '');
  const assessmentKey =
    assessment.assessment_key ?? assessment.key ?? UNKNOWN_ASSESSMENT_KEY;
  const scoreMap = assessmentScoreMaps?.[assessmentId] || {};
  const timestamp = formatAssessmentTimestamp(assessment.inserted_at);

  const categoryLookups = useMemo(
    () => buildRubricCategoryLookups(questionSets),
    [questionSets]
  );

  const includedCategoryKeys = useMemo(
    () => includedCategoryKeysInOrder(questionSets, assessmentKey),
    [questionSets, assessmentKey]
  );

  const showCategoryPills =
    categoryLookups.size > 1 && includedCategoryKeys.length > 0;

  return (
    <div className={classNames(styles['assessment-item'], className)}>
      <div className={styles.header}>
        <div className={styles.assessment}>
          <Link
            to={`/w/${workspaceId}/teams/${assessment.team_id}/assessments/${assessment.assessment_key}/detail/${assessment.assessment_id}`}
          >
            {assessment.assessment_name || assessment.name || 'Assessment'}
          </Link>
        </div>
        <div className={styles.timestamp}>
          <Icon icon={faCalendarAlt} className="icon-left" />
          {timestamp}
        </div>
      </div>

      <div className="assessment-history-chart">
        <div className="assessment-history-questions">
          {showCategoryPills && (
            <div className={categoryLayoutStyles['category-pills']}>
              <CategoryPills
                categories={includedCategoryKeys}
                categoryLookups={categoryLookups}
              />
            </div>
          )}

          <ul className={categoryLayoutStyles['category-layout']}>
            {questionSets.map((questionSet) => {
              const questions = questionSet.questions || [];
              const contributesToSet = questionSetContributesToAssessment(
                questionSet,
                assessmentKey
              );
              const category = getQuestionSetCategory(questionSet);
              const palette = categoryLookups.get(category)?.palette ?? '';

              return (
                <li
                  key={questionSet.id || questionSet.key || questionSet.name}
                  className={classNames(
                    categoryLayoutStyles['question-set'],
                    'assessment-history-question-set'
                  )}
                >
                  <div>
                    <span
                      className={classNames(
                        categoryLayoutStyles['question-set-name'],
                        'question-set-name'
                      )}
                      style={{ opacity: contributesToSet ? 1 : 0.8 }}
                    >
                      {questionSet.name || questionSet.key || 'Question Set'}
                    </span>
                    <div
                      className={classNames(
                        categoryLayoutStyles['name-bar'],
                        palette
                      )}
                    />
                  </div>
                  <div>
                    <ul className="category-question-list">
                      {questions.map((question) => {
                        const sourceForAssessment = (
                          question.sources || []
                        ).find(
                          (source) => source.assessment_key === assessmentKey
                        );
                        const isQuestionFromAssessment = !!sourceForAssessment;
                        const tripleKey = rubricQuestionTripleKey(
                          assessmentKey,
                          sourceForAssessment
                        );
                        const score =
                          tripleKey == null ? null : scoreMap[tripleKey];
                        const missingData = score == null;

                        if (missingData) {
                          return (
                            <li
                              key={
                                question.id || question.guid || question.text
                              }
                              className="assessment-history-question"
                              title={
                                question.text ||
                                question.name ||
                                'Rubric question'
                              }
                              style={{
                                opacity: isQuestionFromAssessment ? 1 : 0.8,
                              }}
                            >
                              <NullMarker
                                width={20}
                                height={20}
                                viewBox="0 0 20 20"
                              />
                            </li>
                          );
                        }

                        return (
                          <li
                            key={question.id || question.guid || question.text}
                            className="assessment-history-question"
                            title={
                              question.text ||
                              question.name ||
                              'Rubric question'
                            }
                            style={{
                              backgroundColor: scaleColorTraffic(score),
                              opacity: isQuestionFromAssessment ? 1 : 0.8,
                            }}
                          />
                        );
                      })}
                    </ul>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
